import { lazyRouteComponent } from "@tanstack/react-router";
import { NAV_SECTIONS, STANDALONE_ROUTES, navGroupsFor } from "@jaks/contract/registry";
import type { NavGroup, NavGroupView, NavSectionDef } from "@jaks/contract/registry";

export type { NavGroup, NavGroupView };

/**
 * A screen the shell can mount: its registry entry, plus how it looks.
 *
 * `group` is optional because not every route is navigation. `/settings` has a
 * screen, an address and a permission and no place in the sidebar — see
 * STANDALONE_ROUTES in the registry.
 */
export interface ModuleDef extends Omit<NavSectionDef, "group"> {
  group?: NavGroup;
  icon: string;
  component: ReturnType<typeof lazyRouteComponent>;
  /** A record screen under `/<id>/<detailPath>`, for a module whose rows have pages of their own (T25: Vendors). */
  detail?: ReturnType<typeof lazyRouteComponent>;
  /**
   * The path under `/<id>/` the record screen mounts at; `$id` when absent.
   * Customer Pipeline uses `c/$accountId` (T43) so a customer's page reads
   * `/sales/c/<id>` and leaves `/sales/<anything else>` to fall to not-found.
   */
  detailPath?: string;
}

/**
 * What each section looks like and which screen it mounts. That is all this
 * file decides now.
 *
 * Who may see it, what it is called, which group it sits in and where in that
 * group belong to the registry in @jaks/contract, resolved per person into
 * `allowedModules` on `/api/me`. Two places deciding visibility is how Settings
 * ended up on every field crew member's phone; two places deciding the groups
 * is how the sidebar and the phone drifted apart.
 */
const CHROME: Record<string, { icon: string; component: ReturnType<typeof lazyRouteComponent>; detail?: ReturnType<typeof lazyRouteComponent>; detailPath?: string }> = {
  home: { icon: "home", component: lazyRouteComponent(() => import("@jaks/module-home/web")) },
  tasks: { icon: "check", component: lazyRouteComponent(() => import("@jaks/module-tasks/web")) },
  calendar: { icon: "calendarDay", component: lazyRouteComponent(() => import("@jaks/module-sales/calendar")) },
  // One section, three views and one record (T43). The record's full page is
  // `/sales/c/<accountId>`; the same record opens as a panel beside any view.
  sales: {
    icon: "funnel",
    component: lazyRouteComponent(() => import("@jaks/module-sales/web")),
    detail: lazyRouteComponent(() => import("@jaks/module-sales/record")),
    detailPath: "c/$accountId",
  },
  estimating: { icon: "ruler", component: lazyRouteComponent(() => import("@jaks/module-estimating/web")) },
  field: { icon: "hardhat", component: lazyRouteComponent(() => import("@jaks/module-field/web")) },
  reports: { icon: "chart", component: lazyRouteComponent(() => import("@jaks/module-reports/web")) },
  vendors: {
    icon: "truck",
    component: lazyRouteComponent(() => import("@jaks/module-vendors/web")),
    detail: lazyRouteComponent(() => import("@jaks/module-vendors/vendor")),
  },
  fleet: { icon: "field", component: lazyRouteComponent(() => import("@jaks/module-fleet/web")) },
  people: { icon: "people", component: lazyRouteComponent(() => import("@jaks/module-people/web")) },
  hr: { icon: "badge", component: lazyRouteComponent(() => import("@jaks/module-hr/web")) },
  learn: { icon: "learn", component: lazyRouteComponent(() => import("@jaks/module-learn/web")) },
  website: { icon: "globe", component: lazyRouteComponent(() => import("@jaks/module-website/web")) },
  executive: { icon: "star", component: lazyRouteComponent(() => import("@jaks/module-executive/web")) },
  settings: { icon: "gear", component: lazyRouteComponent(() => import("@jaks/module-settings/web")) },
  // One frame around `/capture-app/`, the capture app's own web build served
  // from this origin (T33). Not navigation: the entrance is the tool row's tile.
  capture: { icon: "viewfinder", component: lazyRouteComponent(() => import("@jaks/module-capture/web")) },
};

/**
 * THE manifest: every nav section in the registry, in registry order, then the
 * routes that are addresses without being navigation. Each wears its icon and
 * points at its screen; one the registry names but this file has no chrome for
 * fails the drift test rather than mounting blank.
 *
 * Both halves are here because both are routes. Only the first half is ever
 * drawn in a sidebar — `visibleGroups` reads `navGroupsFor`, which only knows
 * about NAV_SECTIONS, so a standalone route cannot leak into navigation by
 * being added to this list.
 */
const withChrome = (s: { id: string }): ModuleDef => {
  const chrome = CHROME[s.id];
  if (!chrome) throw new Error(`no screen mounted for route "${s.id}"`);
  return { ...(s as Omit<ModuleDef, "icon" | "component">), ...chrome };
};

/** The sections drawn in navigation, in registry order. */
export const NAV_MODULES: ModuleDef[] = NAV_SECTIONS.map(withChrome);

/** Every screen with an address, navigation or not. */
export const MODULES: ModuleDef[] = [...NAV_MODULES, ...STANDALONE_ROUTES.map(withChrome)];

const BY_ID = new Map(MODULES.map((m) => [m.id as string, m]));

/**
 * The modules that also mount a record screen under `/<id>/`. Not navigation:
 * nothing here is drawn in a sidebar or a tab bar, and the shell lights the
 * parent section for any path under it (`sectionAt` in the shell).
 */
export const DETAIL_MODULES: ModuleDef[] = MODULES.filter((m) => m.detail);

/** The route pattern a module's record screen mounts at. */
export function detailRouteOf(m: ModuleDef): string {
  return `/${m.id}/${m.detailPath ?? "$id"}`;
}

/**
 * Addresses that used to be sections and are now somewhere inside one.
 *
 * `/customers` was a nav section until T43 folded it into Customer Pipeline as
 * the People view, with the record it opened (`?account=`) becoming the record
 * panel (`?record=`). A saved link, a notification written before the change
 * and a browser's history all still say `/customers`, so the address stays
 * mounted and answers with where the thing went. The router mounts one
 * concrete route per entry here, which is what keeps the not-found splat from
 * catching it: a splat ranks below every concrete path.
 */
export const LEGACY_REDIRECTS: ReadonlyArray<{ from: string; to: (search: URLSearchParams) => string }> = [
  {
    from: "/customers",
    to: (search) => {
      const account = search.get("account");
      return account ? `/sales?record=${encodeURIComponent(account)}` : "/sales?view=people";
    },
  },
];

/** Where a legacy address now lives, or null when the address is not one. */
export function legacyRedirect(pathname: string, searchStr: string): string | null {
  const hit = LEGACY_REDIRECTS.find((r) => r.from === pathname);
  return hit ? hit.to(new URLSearchParams(searchStr)) : null;
}

/**
 * The sections this person may enter, in navigation order. One computation for
 * the sidebar, the mobile tab bar, the More page and the route guard, so they
 * can never disagree about what someone is allowed to open.
 */
export function visibleModules(allowedIds: readonly string[] | undefined): ModuleDef[] {
  if (!allowedIds) return [];
  return MODULES.filter((m) => allowedIds.includes(m.id));
}

/** The same list the sidebar draws: permitted sections under their group headings. */
export function visibleGroups(allowedIds: readonly string[] | undefined): Array<{ group: NavGroup; items: ModuleDef[] }> {
  return navGroupsFor(allowedIds).map((g) => ({
    group: g.group,
    items: g.sections.map((s) => BY_ID.get(s.id)).filter((m): m is ModuleDef => Boolean(m)),
  }));
}

/** The screen a registry section mounts, by id. */
export function moduleOf(id: string): ModuleDef | undefined {
  return BY_ID.get(id);
}
